import { fromJS } from 'immutable'
import type {
  FilterValuesListTypeModel,
  FiltersOrderedMap,
  FilterItemModel
} from '@aytm/app/insights/dashboard/models/types.ClientModel'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { defaultWave, defaultMinimumSampleSize } from '@aytm/app/insights/dashboard/lib/config'
import { getCurrentBlock, updateCurrentBlock, updateCurrentData, updateCurrentSchema, updateState } from './requests'
import type { FilterOptions } from '@aytm/app/insights/dashboard/lib/filters'

export const FILTERS_SAMPLE_SIZE_ID = 'sample:low_size_cutoff'
export const FILTERS_AGE_ID = 'trait:age'

export const SANITIZE_IGNORE_FILTERS = [FILTERS_AGE_ID, FILTERS_SAMPLE_SIZE_ID]

const replaceFilters = (oldFilters: FiltersOrderedMap | null, newFilters: FiltersOrderedMap): FiltersOrderedMap => {
  if (!oldFilters) {
    return newFilters
  }
  let result = oldFilters
  // eslint-disable-next-line immutablejs/no-native-map-set
  const processedKeys = new window.Set()
  // Update/Delete filters
  for (const key of result.keySeq().toArray()) {
    processedKeys.add(key)
    result = newFilters.has(key) ? result.set(key, newFilters.get(key)) : result.delete(key)
  }
  // Add new filters
  for (const key of newFilters.keySeq().toArray()) {
    if (processedKeys.has(key)) {
      continue
    }
    result = result.has(key) ? result : result.set(key, newFilters.get(key))
  }
  return result
}

const replaceAgeFilter = (filters: FiltersOrderedMap): FiltersOrderedMap => {
  let result = filters
  if (filters.has(FILTERS_AGE_ID)) {
    const values = filters.getIn([FILTERS_AGE_ID, 'values']) as FilterValuesListTypeModel
    if (1 === values.count() && '13-1000000' === values.get(0)) {
      result = result.delete(FILTERS_AGE_ID)
    }
  }
  return result
}

export const deleteFilters = (filters: FiltersOrderedMap, skipUids: string[]): FiltersOrderedMap => {
  let result = filters
  if (filters) {
    if (skipUids && skipUids.length) {
      for (const uid of filters.keySeq().toArray()) {
        if (!skipUids.includes(uid as string)) {
          result = result.delete(uid)
        }
      }
      return result
    } else {
      return result.clear()
    }
  }
  return result
}

export const getPortalFilters = (state: InsightsDashboardPoolState): FiltersOrderedMap =>
  state?.currentSchema?.get<FiltersOrderedMap>('filters') || null

export const getPanelFilters = (state: InsightsDashboardPoolState): FiltersOrderedMap => {
  const currentBlockUID = state.currentSchema?.get<string>('currentBlockUID') || null
  return state?.currentSchema?.getIn<FiltersOrderedMap>(['blocks', currentBlockUID, 'filters']) || null
}

export const getAllFilters = (state: InsightsDashboardPoolState): FiltersOrderedMap | null => {
  const portalFilters = getPortalFilters(state)
  const panelFilters = getPanelFilters(state)
  if (portalFilters && panelFilters) {
    return portalFilters.merge(panelFilters)
  } else if (portalFilters) {
    return portalFilters
  } else if (panelFilters) {
    return panelFilters
  } else {
    return null
  }
}

export const getMinSampleSize = (state: InsightsDashboardPoolState) => {
  const block = getCurrentBlock(state)
  if (block) {
    const filters = getPanelFilters(state)
    const sample = Number(filters?.getIn<FilterValuesListTypeModel>([FILTERS_SAMPLE_SIZE_ID, 'values'])?.get(0))
    return isNaN(sample) ? defaultMinimumSampleSize : sample
  } else {
    return defaultMinimumSampleSize
  }
}

export const getMinSampleSizeValue = (filters: FiltersOrderedMap) => {
  if (filters) {
    const sample = Number(filters?.getIn<FilterValuesListTypeModel>([FILTERS_SAMPLE_SIZE_ID, 'values'])?.get(0))
    return isNaN(sample) ? defaultMinimumSampleSize : sample
  } else {
    return 0
  }
}

export const updatePortalFilters = (state: InsightsDashboardPoolState, filters: FiltersOrderedMap) => {
  const oldFilters = getPortalFilters(state)
  const newFilters = replaceFilters(oldFilters, filters)
  return updateCurrentSchema(state, { filters: newFilters })
}

export const updatePanelFilters = (state: InsightsDashboardPoolState, filters: FiltersOrderedMap) => {
  const oldFilters = getPanelFilters(state)
  const newFilters = replaceAgeFilter(replaceFilters(oldFilters, filters))
  return updateCurrentBlock(state, { filters: newFilters })
}

export const resetPanelFilters = (state: InsightsDashboardPoolState) => {
  const disabledFilters = state.currentData?.disabledFilters || []
  const block = getCurrentBlock(state)
  const oldFilters = getPanelFilters(state)
  const newFilters = deleteFilters(oldFilters, disabledFilters)
  const trackAttributes = block?.getJS<'trackAttributes'>('trackAttributes')
  if (trackAttributes) {
    trackAttributes.entity = null
  }
  let resultState = updateCurrentBlock(state, {
    filters: newFilters,
    currentSplit: null,
    trackAttributes,
    trackTarget: 'entity',
    currentWave: defaultWave
  })
  resultState = updateCurrentData(resultState, { panelCurrentFilters: newFilters.clone(), panelFiltersChanged: false })
  return resultState
}

export const updateMinSampleSize = (state: InsightsDashboardPoolState, minSampleSize: number) => {
  const oldFilters = getPanelFilters(state)
  const newMinSampleSize = isNaN(Number(minSampleSize)) ? 0 : Number(minSampleSize)
  const newFilters = oldFilters?.set(FILTERS_SAMPLE_SIZE_ID, {
    uid: FILTERS_SAMPLE_SIZE_ID,
    values: fromJS([newMinSampleSize]) as FilterValuesListTypeModel
  } as FilterItemModel)
  return updateCurrentBlock(state, { filters: newFilters })
}

const sanitize = (filters: FiltersOrderedMap, filterOptions: FilterOptions): [boolean, FiltersOrderedMap] => {
  let result = filters
  const options = Object.values(filterOptions)
    .flat()
    .map(o => {
      return { uid: o.id, values: o.options.map(opt => String(opt.value)) }
    })
  const deleteEntries = {} as Record<string, string[] | null>
  for (const filter of filters.valueSeq().toArray()) {
    const uid = filter.get('uid')
    if (!SANITIZE_IGNORE_FILTERS.includes(uid)) {
      const option = options.find(o => o.uid === uid)
      if (option) {
        for (const value of filter.get('values').toArray()) {
          if (!option.values.includes(String(value))) {
            if (!deleteEntries[uid]) {
              deleteEntries[uid] = []
            }
            deleteEntries[uid].push(String(value))
          }
        }
      } else {
        deleteEntries[uid] = null
      }
    }
  }
  for (const uid of Object.keys(deleteEntries)) {
    const valuesToDelete = deleteEntries[uid]
    if (valuesToDelete === null) {
      result = result.delete(uid)
    } else {
      for (const value of valuesToDelete) {
        const currentValues = result.getIn<FilterValuesListTypeModel>([uid, 'values']).toArray()
        const index = currentValues.indexOf(value)
        if (index > -1) {
          result = result.deleteIn([uid, 'values', index])
        }
      }
      if (result.getIn<FilterValuesListTypeModel>([uid, 'values']).size === 0) {
        result = result.delete(uid)
      }
    }
  }
  return [Object.keys(deleteEntries).length > 0, result]
}

export const sanitizeSources = (state: InsightsDashboardPoolState, sourceOptions: FilterOptions) => {
  let resultState = state
  const currentSources = state.currentSchema?.get<FiltersOrderedMap>('filters')
  if (currentSources) {
    const [modified, newSources] = sanitize(currentSources, sourceOptions)
    if (modified) {
      resultState = updateCurrentSchema(resultState, { filters: newSources })
      resultState = updateState(resultState, { portalModified: true })
      const portalCurrentFilters = resultState.currentData?.portalCurrentFilters
      if (portalCurrentFilters) {
        const [modified, newFilters] = sanitize(portalCurrentFilters, sourceOptions)
        if (modified) {
          resultState = updateCurrentData(resultState, {
            portalCurrentFilters: newFilters,
            panelFiltersChanged: false
          })
        }
      }
    }
  }
  return resultState
}

export const sanitizeFilters = (state: InsightsDashboardPoolState, filterOptions: FilterOptions) => {
  let resultState = state
  const block = getCurrentBlock(state)
  const currentFilters = block?.get<FiltersOrderedMap>('filters')
  if (!currentFilters) {
    const [modified, newFilters] = sanitize(currentFilters, filterOptions)
    if (modified) {
      resultState = updateCurrentBlock(resultState, { filters: newFilters })
      const panelCurrentFilters = resultState.currentData?.panelCurrentFilters
      if (panelCurrentFilters) {
        const [modified, newFilters] = sanitize(panelCurrentFilters, filterOptions)
        if (modified) {
          resultState = updateCurrentData(resultState, {
            panelCurrentFilters: newFilters,
            panelFiltersChanged: false
          })
        }
      }
    }
  }
  return resultState
}

export const getPanelCurrentFiltersCount = (state: InsightsDashboardPoolState): number => {
  const panelCurrentFilters = state.currentData?.panelCurrentFilters
  return panelCurrentFilters?.filter(f => FILTERS_SAMPLE_SIZE_ID !== f.get('uid')).size || 0
}

export const getPortalCurrentFiltersCount = (state: InsightsDashboardPoolState): number => {
  const portalCurrentFilters = state.currentData?.portalCurrentFilters
  return portalCurrentFilters?.size || 0
}
